import type { Api } from '@rialtic/api'
import { useIDBKeyval } from '@vueuse/integrations/useIDBKeyval'
import { get, set } from 'idb-keyval'
import type { FetchOptions } from 'ofetch'
import { acceptHMRUpdate, defineStore } from 'pinia'

const sortByName = (a: Pick<Api.Policy, 'name'>, b: Pick<Api.Policy, 'name'>) =>
  a.name?.localeCompare(b?.name)

const IDB_POLICIES_KEY = <const>'policies'
const IDB_PINNED_POLICIES_KEY = <const>'pinned-policies'
const IDB_RECENT_POLICIES_KEY = <const>'recent-policies'

export const useEditor = defineStore('main', {
  state: () => {
    const { data: pinnedPolicyIds } = useIDBKeyval<string[]>(
      IDB_PINNED_POLICIES_KEY,
      [],
    )
    const { data: recentPolicyIds } = useIDBKeyval<string[]>(
      IDB_RECENT_POLICIES_KEY,
      [],
    )
    return {
      pinnedPolicyIds,
      recentPolicyIds,
      policies: {} as Record<string, Api.Policy>,
      lastFetchedAt: 0,
    }
  },

  getters: {
    allPolicies: (state) => Object.values(state.policies),

    pinnedPolicyIndex: (state) => (policyId: string) =>
      state.pinnedPolicyIds.findIndex((id) => id === policyId),

    pinnedPolicies: (state): Api.Policy[] =>
      state.pinnedPolicyIds
        .map((id) => {
          const { name, latest_review_status } = state.policies[id] || {}
          return {
            id,
            name,
            latest_review_status,
          } as unknown as Api.Policy
        })
        .sort(sortByName),

    policyIsPinned(): (policyId: string) => boolean {
      return (policyId: string) => this.pinnedPolicyIndex(policyId) >= 0
    },

    recentPolicies: (state): Api.Policy[] =>
      state.recentPolicyIds.map((id) => {
        const { name, latest_review_status } = state.policies[id] || {}
        return {
          id,
          name,
          latest_review_status,
        } as unknown as Api.Policy
      }),
  },

  actions: {
    async $apiFetch<T>(
      url: string,
      opts?: FetchOptions<'json'>,
      bareApi = false,
    ) {
      const config = useRuntimeConfig().public

      const client = $apiFetcher({
        ...opts,
        baseURL: `${config.WORKERS_API_BASE_URL}${
          bareApi ? '' : '/policy-manager'
        }`,
      } as any)

      return client<T>(url)
    },

    async $userAgentFetch<T>(url: string, opts?: FetchOptions<'json'>) {
      const config = useRuntimeConfig().public

      const client = $apiFetcher({
        ...opts,
        baseURL: `${config.WORKERS_API_BASE_URL}/user-agent`,
      } as any)

      return client<T>(url)
    },

    async syncPinnedPoliciesToAgent() {
      const { $groundcover } = useNuxtApp()

      let ids: string[] = []

      try {
        ids = (await get<string[]>(IDB_PINNED_POLICIES_KEY)) ?? []
      } catch (e) {
        $groundcover?.captureException(e as Error, {
          reason: 'Policy favorites sync error',
        })
      }
      if (!ids.length) return

      const results = await Promise.allSettled(
        ids.map((id) =>
          this.$userAgentFetch('/favorites', {
            method: 'POST',
            body: { itemType: 'policies', itemId: id },
          }),
        ),
      )

      const failures = results.filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      )
      if (failures.length) {
        for (const f of failures) {
          $groundcover?.captureException(new Error(f.reason))
        }
      }
    },

    async _syncPolicyPinToAgent(policyId: string, isPinning: boolean) {
      try {
        if (isPinning) {
          await this.$userAgentFetch('/favorites', {
            method: 'POST',
            body: { itemType: 'policies', itemId: policyId },
          })
        } else {
          await this.$userAgentFetch('/favorites', {
            method: 'DELETE',
            query: { itemType: 'policies', itemId: policyId },
          })
        }
      } catch (error) {
        const { $groundcover } = useNuxtApp()
        $groundcover?.captureException(error as Error)
      }
    },

    async getPolicies({ forceFetch = false }: { forceFetch?: boolean } = {}) {
      const shouldFetch = !this.allPolicies.length || forceFetch
      const mins15 = 15 * 60 * 1000
      if (!shouldFetch && Date.now() - this.lastFetchedAt < mins15) {
        return this.allPolicies
      }

      if (!this.allPolicies.length) {
        const idbPolicies = await get(IDB_POLICIES_KEY)
        if (idbPolicies?.length) {
          this.setPolicies(idbPolicies)
        }
      }

      const res = await this.$apiFetch<Api.Policy[]>('/policy')

      this.lastFetchedAt = Date.now()
      this.setPolicies(res)

      return res
    },

    async getPolicy(id: string) {
      const res = await this.$apiFetch<Api.Policy>(
        `/policy/${id}`,
        undefined,
        true,
      )

      this.setPolicies([res])

      return res
    },

    pushToRecentPolicy(policyId: string) {
      const recentPolicies = [
        policyId,
        ...this.recentPolicyIds.filter((id) => id !== policyId),
      ].slice(0, 10)

      this.recentPolicyIds = recentPolicies
    },

    async setPolicies(policies: Api.Policy[]) {
      const { workerFn } = useWebWorkerFn(
        (data) => {
          const { policies, currentState } = JSON.parse(data)
          return policies.reduce((state: any, policy: any) => {
            state[policy.id] = policy
            return state
          }, currentState)
        },
        {
          timeout: 50_000,
        },
      )

      const res = await workerFn(
        JSON.stringify({ policies, currentState: this.policies }),
      )

      this.policies = res

      const { $groundcover } = useNuxtApp()

      if (!res) {
        return
      }
      try {
        await set(IDB_POLICIES_KEY, Object.values(res))
      } catch (error) {
        $groundcover.captureException(error as Error)
      }
    },

    togglePolicyPin(policyId: string) {
      const index = this.pinnedPolicyIndex(policyId)
      const isPinning = index < 0
      if (isPinning) {
        this.pinnedPolicyIds.push(policyId)
      } else {
        this.pinnedPolicyIds.splice(index, 1)
      }
      // Fire-and-forget: don't block local pinning on agent sync
      this._syncPolicyPinToAgent(policyId, isPinning).catch(() => {})
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useEditor, import.meta.hot))
